/**
 * Turn an axios blob response into a browser download.
 *
 * We can't point the browser straight at an export URL: the API needs a Bearer
 * token in the Authorization header, and putting the JWT in a query string
 * would leak it into browser history and HTTP logs. So the file is fetched as
 * a blob and an <a> click is synthesised instead.
 *
 * @param {Object} res axios response with responseType: 'blob'
 * @param {string} fallbackName Filename to use when the server sends none
 */
export function downloadBlobResponse(res, fallbackName) {
  const blob = new Blob([res.data], {
    type: res.headers?.['content-type'] || 'application/octet-stream',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filenameFromResponse(res) || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Read the filename the API chose out of Content-Disposition. Returns null when
 * the header is absent — CORS hides it unless the server also sends
 * Access-Control-Expose-Headers.
 *
 * @param {Object} res axios response
 * @returns {string|null}
 */
export function filenameFromResponse(res) {
  const disposition = res.headers?.['content-disposition'];
  if (!disposition) {
    return null;
  }

  const match = /filename="?([^";]+)"?/i.exec(disposition);

  return match ? match[1].trim() : null;
}

/**
 * An error thrown by a blob request carries its JSON body as a Blob too, so the
 * API's message has to be read back out before it can be shown to the user.
 *
 * @param {Object} error axios error
 * @returns {Promise<string|null>}
 */
export function readBlobErrorMessage(error) {
  const data = error?.response?.data;

  if (!(data instanceof Blob)) {
    return Promise.resolve(error?.response?.data?.message || null);
  }

  return data
    .text()
    .then((text) => {
      try {
        return JSON.parse(text).message || null;
      } catch (e) {
        return null;
      }
    })
    .catch(() => null);
}
